import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, CheckCircle2, MessageSquare, Phone, Zap } from 'lucide-react';
import { getIndustryBySlug } from '../data/industriesData';
import SEO from '../components/SEO';
import { trackIndustryView } from '../utils/analytics';

const serviceIcons = { chatbot: MessageSquare, receptionist: Phone, automation: Zap };

const IndustryPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const industry = getIndustryBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (industry) trackIndustryView(slug, industry.title);
  }, [slug, industry]);

  if (!industry) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Industry Not Found</h1>
          <button onClick={() => navigate('/')} className="text-[#A1A1AA] hover:text-white underline">Back to Home</button>
        </div>
      </div>
    );
  }

  const Icon = industry.icon;

  return (
    <div className="min-h-screen bg-[#050505] pt-20" data-testid="industry-page">
      <div className="orange-orb orange-orb-1 top-32 -left-32 w-[28rem] h-[28rem] z-0" />
      <div className="orange-orb orange-orb-2 top-1/2 -right-32 w-[32rem] h-[32rem] z-0" />
      <div className="relative z-10">
      <SEO
        title={`AI Solutions for ${industry.title}`}
        description={`${industry.tagline} Agentic Atoms builds AI chatbots, AI voice agents, AI receptionists, and AI sales agents for ${industry.title.toLowerCase()}. Get a working AI system in 30 days.`}
        keywords={`AI for ${industry.title.toLowerCase()}, AI chatbot for ${industry.title.toLowerCase()}, AI receptionist for ${industry.title.toLowerCase()}, AI voice agent, AI sales agent, AI automation, ${industry.title}`}
        path={`/industries/${slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": `AI Chatbots, AI Voice Agents & AI Solutions for ${industry.title}`,
          "description": industry.overview,
          "provider": { "@type": "Organization", "name": "Agentic Atoms", "url": "https://agenticatoms.com" },
          "serviceType": ["AI Chatbot", "AI Voice Agent", "AI Receptionist", "AI Sales Agent", "Workflow Automation"]
        }}
      />

      {/* Hero */}
      <section className="bg-[#050505] py-16">
        <div className="max-w-5xl mx-auto px-6">
          <Link to="/" className="inline-flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors mb-8 text-sm" data-testid="industry-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-[#FF6B00]/15 border border-[#FF6B00]/30 flex items-center justify-center">
                <Icon className="w-6 h-6 text-[#FF6B00]" />
              </div>
              <span className="text-xs font-bold uppercase tracking-widest text-[#71717A]">
                {industry.tab === 'small' ? 'Local Business' : 'Mid-Size Business'}
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-4" data-testid="industry-page-title">
              AI Solutions for {industry.title}
            </h1>
            <p className="text-[#A1A1AA] text-base md:text-lg max-w-2xl leading-relaxed" data-testid="industry-page-tagline">
              {industry.tagline}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 border-b border-white/[0.08]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-3 gap-4">
            {industry.stats.map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + i * 0.1 }}
                className="bg-white/[0.03] backdrop-blur-md rounded-2xl p-5 text-center border border-white/[0.08] card-glow-hover"
                data-testid={`industry-page-stat-${i}`}
              >
                <p className="text-2xl md:text-3xl font-bold text-[#FF6B00]">{stat.value}</p>
                <p className="text-xs text-[#A1A1AA] mt-1">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-14">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <h2 className="text-2xl font-bold text-white mb-4">The Challenge</h2>
            <p className="text-[#A1A1AA] leading-relaxed max-w-3xl">{industry.overview}</p>
          </motion.div>
        </div>
      </section>

      {/* Services - How We Help */}
      <section className="py-14 bg-[#050505]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-white mb-8">How Agentic Atoms Helps</h2>
          <div className="grid md:grid-cols-2 gap-5">
            {industry.services.map((service, i) => {
              const SIcon = serviceIcons[service.key];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.1 }}
                  className="bg-[#050505] rounded-2xl p-6 border border-white/[0.08] hover:shadow-md transition-shadow"
                  data-testid={`industry-page-service-${i}`}
                >
                  <div className="flex items-center gap-2.5 mb-3">
                    <div className="w-9 h-9 rounded-lg bg-[#FF6B00]/15 border border-[#FF6B00]/25 flex items-center justify-center">
                      <SIcon className="w-4 h-4 text-[#FF6B00]" />
                    </div>
                    <h3 className="font-semibold text-white text-sm">{service.name}</h3>
                  </div>
                  <p className="text-sm text-[#A1A1AA] leading-relaxed">{service.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-14">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-white mb-8">Real-World Use Cases</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {industry.useCases.map((uc, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + i * 0.08 }}
                className="flex items-start gap-3 bg-white/[0.03] rounded-xl p-4"
                data-testid={`industry-page-usecase-${i}`}
              >
                <CheckCircle2 className="w-5 h-5 text-[#FF6B00] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-[#A1A1AA] leading-relaxed">{uc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-[#050505]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
              Ready to Transform Your {industry.title.split(' & ')[0]}?
            </h2>
            <p className="text-[#A1A1AA] text-sm mb-6">Free 30-minute discovery call. We'll show you exactly where AI can help.</p>
            <a
              href="https://calendly.com/agenticatoms/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-black rounded-full font-medium shadow-[0_8px_32px_rgba(0,0,0,0.45)] hover:bg-[#444] transition-colors"
              data-testid="industry-page-cta"
            >
              Book a Free Discovery Call
              <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </section></div>
    </div>
  );
};

export default IndustryPage;
